import { FILES_FIELD_DEFAULT_MAX, formatBytes, type RecordFileItem } from '@ofmos/shared/files';
import type { FieldMetadata, FieldValue } from '../../data/client';
import type { IconName } from '../../ui';

/**
 * The small facts every attachment surface needs, in one place
 * (`docs/decisions/file-attachments.md` D6, D18).
 *
 * The record page, the table cell, the cell editor and the phone sheet all ask the same four
 * questions - what is in this cell, is this one an image, what glyph does it get, how many
 * more will it take - and four answers would be four chances to disagree about the cap.
 */

/** D5's inline allowlist, and the only types that get a preview (D6). */
const PREVIEWABLE: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

export function isPreviewable(item: RecordFileItem): boolean {
  return PREVIEWABLE.has(item.contentType);
}

/**
 * TWO GLYPHS, and that is the whole table.
 *
 * The distinction a reader needs is "can I see this without opening it" - an image carries
 * its own preview, everything else is a paperclip. A per-mime-type table was the first
 * version and it was worse in a way a screenshot showed immediately: the nearest glyph this
 * set has to a document is `info`, so every PDF in the list wore an information badge.
 *
 * The set ships about thirty glyphs on purpose (`Icon.tsx`: "a 1,500-glyph library invites
 * drift and grows the bundle for the twenty glyphs a CRM actually uses"), so the honest
 * answer is two categories rather than a wrong glyph per type.
 */
export function fileIcon(item: RecordFileItem): IconName {
  return isPreviewable(item) ? 'eye' : 'paperclip';
}

/** The cell's value as items, tolerating the null a never-written field holds. */
export function fileItemsOf(value: FieldValue): readonly RecordFileItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is RecordFileItem =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as { fileId?: unknown }).fileId === 'string',
  );
}

/** D10, D21: the field's own cap, or the default when an admin has not set one. */
export function maxFilesFor(field: FieldMetadata): number {
  return field.options?.maxFiles ?? FILES_FIELD_DEFAULT_MAX;
}

export function remainingFor(field: FieldMetadata, count: number): number {
  return Math.max(0, maxFilesFor(field) - count);
}

/** "Contract.pdf, 1.2 MB" - the one line a row, a chip and a sheet header all read. */
export function fileSummary(item: RecordFileItem): string {
  return `${item.label}, ${formatBytes(item.byteCount)}`;
}
